import { Injectable, signal } from '@angular/core';

export type MvaFilter = '' | 'yes' | 'no';
export type TaxFilter = '' | 'q' | 'nq';
export type SortField = 'rate' | 'rating' | 'carrier' | 'min';
export type StrategyView = 'capped' | 'uncapped' | 'all';
export type ProtectionType = '' | 'buffer' | 'floor';
export type SourceType = '' | 'rila' | 'hybrid';

/**
 * Shared filter state for the RILA Rate Sheet (/rila-rate-sheet).
 *
 * Same root-singleton split as the Index sheet (rail writes, page reads), with
 * Downside Protection additions RILA needs.
 */
@Injectable({ providedIn: 'root' })
export class RilaRateSheetStateService {
  readonly premium = signal(100_000);
  readonly clientAge = signal<number | null>(null);
  readonly termMin = signal<number | null>(null);
  readonly termMax = signal<number | null>(null);
  readonly minRating = signal(1);
  readonly mvaFilter = signal<MvaFilter>('');
  readonly stateFilter = signal('');
  readonly taxFilter = signal<TaxFilter>('');
  readonly sortField = signal<SortField>('rate');
  readonly showAll = signal(false);

  readonly changeUp = signal(false);
  readonly changeDown = signal(false);
  readonly changeFuture = signal(false);

  readonly selectedIndexes = signal<string[]>([]);
  readonly strategyView = signal<StrategyView>('capped');
  readonly availableIndexes = signal<{ name: string; count: number }[]>([]);

  readonly sourceType = signal<SourceType>('');
  readonly protectionType = signal<ProtectionType>('');
  readonly protectionMin = signal<number | null>(null);
  readonly availableBuffers = signal<number[]>([]);
  readonly availableFloors = signal<number[]>([]);

  readonly resultCount = signal(0);
  readonly carrierCount = signal(0);
  readonly availableTerms = signal<number[]>([]);

  captureFilterState(): Record<string, any> {
    return {
      premium: this.premium(),
      clientAge: this.clientAge(),
      termMin: this.termMin(),
      termMax: this.termMax(),
      minRating: this.minRating(),
      mvaFilter: this.mvaFilter(),
      stateFilter: this.stateFilter(),
      taxFilter: this.taxFilter(),
      sortField: this.sortField(),
      selectedIndexes: this.selectedIndexes(),
      strategyView: this.strategyView(),
      sourceType: this.sourceType(),
      protectionType: this.protectionType(),
      protectionMin: this.protectionMin(),
      changeUp: this.changeUp(),
      changeDown: this.changeDown(),
      changeFuture: this.changeFuture(),
    };
  }

  applyFilterState(s: Record<string, any>): void {
    if (s['premium'] != null) this.premium.set(s['premium']);
    this.clientAge.set(s['clientAge'] ?? null);
    this.termMin.set(s['termMin'] ?? null);
    this.termMax.set(s['termMax'] ?? null);
    this.minRating.set(s['minRating'] ?? 0);
    this.mvaFilter.set(s['mvaFilter'] ?? '');
    this.stateFilter.set(s['stateFilter'] ?? '');
    this.taxFilter.set(s['taxFilter'] ?? '');
    if (s['sortField']) this.sortField.set(s['sortField']);
    if (s['selectedIndexes']) this.selectedIndexes.set(s['selectedIndexes']);
    if (s['strategyView']) this.strategyView.set(s['strategyView']);
    this.sourceType.set(s['sourceType'] ?? '');
    this.protectionType.set(s['protectionType'] ?? '');
    this.protectionMin.set(s['protectionMin'] ?? null);
    this.changeUp.set(s['changeUp'] ?? false);
    this.changeDown.set(s['changeDown'] ?? false);
    this.changeFuture.set(s['changeFuture'] ?? false);
  }
}
